import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import { addTab, removeTab, setActiveTab } from "../../../redux/slices/claimsSlice";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import ClaimsTable from "./TabContent/ClaimsTable";
import ClaimTabContent from "./TabContent/ClaimTabContent";
import { getClaimsAccess } from "./claimsAccess";
import { openShipmentDetailTab } from "./claimsNavigation";
import { CLAIM_STATUSES, CLAIM_TYPES } from "./claimsShared";

const ClaimsDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const access = useMemo(() => getClaimsAccess(), []);
  const { tabs, activeTab, defaultTabsCount } = useSelector(selectModuleTabs("claims"));

  // Lives at module level (not inside ClaimsTable) so it stays visible across every
  // tab, the same way Shipment's New/Search/count row sits above its own Tabs.
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [typeFilter, setTypeFilter] = useState("All Claim Types");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalClaims, setTotalClaims] = useState(0);
  // Bumped after any claim save so the (kept-alive) Claims List tab refetches
  // instead of showing stale rows once the user switches back to it.
  const [refreshKey, setRefreshKey] = useState(0);

  const canManageClaims = access.canCreateClaim;

  const openClaimTab = useCallback(
    (claim) => {
      dispatch(
        addTab({
          id: `claim-${claim.claimNumber}-${Date.now()}`,
          name: claim.claimNumber,
          contentType: "claimForm",
          claimNumber: claim.claimNumber,
          officeCode: claim.officeCode || access.officeCode,
          isDynamic: true,
          createdAt: Date.now(),
        })
      );
    },
    [dispatch, access.officeCode]
  );

  const openNewClaimTab = useCallback(() => {
    if (!canManageClaims) return;
    dispatch(
      addTab({
        id: `new-claim-${Date.now()}`,
        name: "New Claim",
        contentType: "claimForm",
        claimNumber: null,
        officeCode: access.officeCodes?.join(",") || access.officeCode,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  }, [dispatch, canManageClaims, access.officeCodes, access.officeCode]);

  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) return;
    dispatch(removeTab({ tabIndex: index }));
  };

  // Looked up by id (not a captured index) so a stale closure from an earlier
  // render can't close the wrong tab after other tabs have opened/closed.
  const closeTabById = useCallback(
    (tabId) => {
      const index = tabs.findIndex((t) => t.id === tabId);
      if (index >= defaultTabsCount) {
        dispatch(removeTab({ tabIndex: index }));
      }
    },
    [tabs, defaultTabsCount, dispatch]
  );

  const defaultTabContents = {
    "claims-list": (
      <ClaimsTable
        key="claims-list"
        access={access}
        refreshKey={refreshKey}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        searchQuery={searchQuery}
        onTotalClaimsChange={setTotalClaims}
        onSelectClaim={openClaimTab}
        onShipmentClick={(claim) =>
          openShipmentDetailTab(
            dispatch,
            navigate,
            claim.shipmentNumber,
            claim.shipmentOfficeCode || access.officeCode
          )
        }
      />
    ),
  };

  return (
    <div className="p-fluid-1 shadow-md flex flex-col font-roboto flex-1 h-full min-h-0 overflow-hidden">
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1 gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            text={statusFilter}
            options={["All Statuses", ...CLAIM_STATUSES]}
            selectedValue={statusFilter}
            onSelect={setStatusFilter}
          />
          <FilterButton
            text={typeFilter}
            options={["All Claim Types", ...CLAIM_TYPES]}
            selectedValue={typeFilter}
            onSelect={setTypeFilter}
          />
          <SearchBox
            value={searchInput}
            onChange={setSearchInput}
            onSearch={() => setSearchQuery(searchInput)}
            placeholder="Search Claims"
          />
          <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
            {totalClaims} Claims
          </span>
        </div>

        {canManageClaims && <Button text="Create Claim" onClick={openNewClaimTab} />}
      </div>

      <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
        <Tabs
          tabs={tabs.map((t) => t.name)}
          tabContents={tabs.map((tab) => {
            if (tab.contentType === "claimForm") {
              return (
                <ClaimTabContent
                  key={tab.id}
                  claimNumber={tab.claimNumber}
                  officeCode={tab.officeCode}
                  access={access}
                  onClose={() => closeTabById(tab.id)}
                  onSaved={() => setRefreshKey((k) => k + 1)}
                />
              );
            }
            return defaultTabContents[tab.id] || null;
          })}
          activeTab={activeTab}
          onTabChange={(index) => dispatch(setActiveTab({ index }))}
          isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}
          keepAlive={tabs.map((tab) => tab.id === "claims-list")}
        />
      </div>
    </div>
  );
};

export default ClaimsDetails;
